import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, imgUrl, safeHtml, sydDate } from "@/lib/api";
import { Field, Notice, PageShell } from "@/components/site/Layout";
import { useJsonLd } from "@/components/site/Seo";

const plain = (h) => { const d = document.createElement("div"); d.innerHTML = safeHtml(h).__html; return d.textContent.trim(); };

export function ContentPage({ type, slug: fixed }) {
  const { slug: p } = useParams();
  const slug = fixed || p;
  const [c, setC] = useState(undefined);
  useEffect(() => { setC(undefined); api.get(`/content/${type}/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [type, slug]);
  if (c === undefined) return <PageShell narrow><p>Loading…</p></PageShell>;
  if (c === null) return (
    <PageShell narrow><h1 className="text-4xl mb-6 capitalize">{slug.replace(/-/g, " ")}</h1>
      <Notice tone="warn" testid="content-unpublished">This page is being finalised and hasn't been published yet. Contact us if you have a question.</Notice></PageShell>
  );
  return (
    <PageShell narrow>
      {type === "journey" && <Link to="/journey" className="text-sm underline">← Our journey</Link>}
      <h1 className="text-4xl sm:text-5xl mb-8 mt-2" data-testid="content-title">{c.title}</h1>
      {c.cover_image && <img src={imgUrl(c.cover_image)} alt="" className="rounded-lg mb-8 w-full" />}
      <div className="prose-hn" data-testid="content-body" dangerouslySetInnerHTML={safeHtml(c.body_html)} />
      {type === "policy" && <p className="text-xs text-[#5b6b62] mt-10">Last updated {sydDate(c.updated_at, false)}</p>}
    </PageShell>
  );
}

export function Journey() {
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/content/journey").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  return (
    <PageShell>
      <p className="eyebrow mb-3">Our journey</p>
      <h1 className="text-4xl sm:text-5xl mb-10">From idea to jar</h1>
      {!posts ? <p>Loading…</p> : posts.length === 0 ? <p className="text-[#3d5248]" data-testid="journey-empty">We'll share updates here as HONEY NUTS gets ready.</p> : (
        <div className="grid md:grid-cols-2 gap-8">
          {posts.map((p) => (
            <Link key={p.id} to={`/journey/${p.slug}`} className="hn-card overflow-hidden hover:border-[#A66B24]" data-testid="journey-post">
              {p.cover_image ? <img src={imgUrl(p.cover_image)} alt="" className="aspect-[16/9] object-cover w-full" /> : <div className="aspect-[16/9] honeycomb-bg" />}
              <div className="p-6"><p className="text-xs text-[#5b6b62]">{sydDate(p.publish_at || p.created_at, false)}</p><h2 className="text-2xl mt-1">{p.title}</h2></div>
            </Link>
          ))}
        </div>
      )}
    </PageShell>
  );
}

export function Faq() {
  const [faqs, setFaqs] = useState(null);
  useEffect(() => { api.get("/content/faq").then((r) => setFaqs(r.data)).catch(() => setFaqs([])); }, []);
  const ld = useMemo(() => (faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.title, acceptedAnswer: { "@type": "Answer", text: plain(f.body_html) } })),
  } : null), [faqs]);
  useJsonLd("faq", ld);
  return (
    <PageShell narrow>
      <h1 className="text-4xl sm:text-5xl mb-10">Frequently asked questions</h1>
      {!faqs ? <p>Loading…</p> : faqs.map((f) => (
        <section key={f.id} className="border-b border-[#E5DEC9] py-6" data-testid="faq-item"><h2 className="text-2xl mb-2">{f.title}</h2><div className="prose-hn text-sm" dangerouslySetInnerHTML={safeHtml(f.body_html)} /></section>
      ))}
    </PageShell>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", order_ref: "", subject: "", message: "", website: "" });
  const [st, setSt] = useState({});
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try { await api.post("/contact", { ...f, order_ref: f.order_ref || null }); setSt({ done: true }); } catch (err) { setSt({ error: errMsg(err) }); }
  };
  return (
    <PageShell narrow>
      <h1 className="text-4xl sm:text-5xl mb-4">Contact</h1>
      <p className="text-[#3d5248] mb-10">Questions about HONEY NUTS or an order? Send us a message and we'll reply by email.</p>
      {st.done ? <Notice testid="contact-success">Thanks — your message has been received.</Notice> : (
        <form onSubmit={submit} className="grid gap-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" id="c-n"><input id="c-n" required className="hn-input" value={f.name} onChange={on("name")} data-testid="contact-name-input" /></Field>
            <Field label="Email" id="c-e"><input id="c-e" type="email" required className="hn-input" value={f.email} onChange={on("email")} data-testid="contact-email-input" /></Field>
          </div>
          <Field label="Order number (optional)" id="c-o"><input id="c-o" className="hn-input" value={f.order_ref} onChange={on("order_ref")} data-testid="contact-order-input" /></Field>
          <Field label="Subject" id="c-s"><input id="c-s" required className="hn-input" value={f.subject} onChange={on("subject")} data-testid="contact-subject-input" /></Field>
          <Field label="Message" id="c-m"><textarea id="c-m" required rows={6} className="hn-input" value={f.message} onChange={on("message")} data-testid="contact-message-input" /></Field>
          <input type="text" className="hidden" tabIndex={-1} aria-hidden autoComplete="off" value={f.website} onChange={on("website")} />
          {st.error && <Notice tone="error" testid="contact-error">{st.error}</Notice>}
          <button className="hn-btn justify-self-start" disabled={st.loading} data-testid="contact-submit-btn">{st.loading ? "Sending…" : "Send message"}</button>
        </form>
      )}
    </PageShell>
  );
}
